/**
 * Location Page V2 (LOCATION-PAGE.md) — pure view-models for the rebuilt
 * /sites/:id working surface. Kept free of React so the doctrine bits are
 * unit-testable: create actions always carry customer + site, the reconcile
 * entry point never disappears (a site with zero devices is exactly where
 * you paste a city list), and the payment chip mirrors the customer page.
 */

export type PaymentProfile = "auto_charge" | "card_on_file_manual" | "net_terms" | "pay_on_invoice";

export interface PaymentChipModel {
    label: string;
    /** Tailwind-agnostic tone the header maps to chip classes. */
    tone: "emerald" | "sky" | "amber" | "muted";
}

/**
 * The payment-profile chip beside "Part of {Customer}" (spec item 1).
 * Falls back to the card-on-file signal when no explicit profile is set;
 * returns null only when we know nothing about how this customer pays.
 */
export function paymentProfileChip(
    profile: string | null | undefined,
    netTermsDays: number | null | undefined,
    hasCardOnFile: boolean | null | undefined,
): PaymentChipModel | null {
    switch (profile) {
        case "auto_charge":
            return { label: "Auto-charge", tone: "emerald" };
        case "card_on_file_manual":
            return { label: "Card on file", tone: "sky" };
        case "net_terms":
            return { label: netTermsDays ? `Net ${netTermsDays}` : "Net terms", tone: "amber" };
        case "pay_on_invoice":
            return { label: "Pay on invoice", tone: "muted" };
        default:
            return hasCardOnFile ? { label: "Card on file", tone: "sky" } : null;
    }
}

/** New Job prefilled with customer + site — the form derives the water authority from the site. */
export function newJobHref(customerId: string, siteId: string): string {
    return `/jobs/new?customerId=${customerId}&siteId=${siteId}`;
}

/** New Estimate prefilled with customer + site (spec item 5: never land on a blank form). */
export function newEstimateHref(customerId: string, siteId: string): string {
    return `/commercial/new?kind=estimate&customerId=${customerId}&siteId=${siteId}`;
}

/** Where the unpaid-invoices chip lands: the documents list filtered to this site. */
export function unpaidInvoicesHref(siteId: string): string {
    return `/commercial?tab=outstanding&siteId=${siteId}`;
}

/**
 * Spec item 3: the [Reconcile from list] entry point is ALWAYS available —
 * including (especially) when the site has zero service lines, because an
 * empty site is exactly where the office pastes a BSI/city device list.
 */
export function reconcileEntryVisible(_serviceLineCount: number): boolean {
    return true;
}

export interface OpenEstimateRow {
    id: string;
    display_number: string | null;
    title: string | null;
    status: string;
    subtotal: string;
    updated_at: string;
}

export type EstimateAction = "approve" | "convert" | "review_convert" | "none";

/**
 * Which inline action an open estimate gets on the site page (spec item 2):
 * draft/sent → Approve (office approval, same PATCH the estimate page uses);
 * accepted   → the Prompt 22 conversion actions, with needs_review rows
 *              forced through the match-review dialog (never silent).
 */
export function estimateInlineAction(
    status: string,
    matchStatus: "clean" | "needs_review" | "no_serials" | undefined,
): EstimateAction {
    if (status === "draft" || status === "sent") return "approve";
    if (status === "accepted") return matchStatus === "needs_review" ? "review_convert" : "convert";
    return "none";
}
